import { useEffect, useRef, useState } from 'react';
import isNil from 'lodash/isNil';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';

interface Params {
    value: string | number;
    isControlled: boolean;
    name?: string;
    autoFocus?: boolean;
    onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
    onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
}

interface Data {
    isOnFocus: boolean;
    inputValue: string | number;
    setInputElement(element: HTMLElement): void;
    commitValue(value: string): void;
    handleFocus(event: React.FocusEvent<HTMLInputElement>): Promise<void>;
    handleBlur(event: React.FocusEvent<HTMLInputElement>): Promise<void>;
}

export function useInput({ value, isControlled, name, autoFocus, onBlur, onFocus }: Params): Data {
    const focusState = useCheckOnFocus();
    const [currentValue, setCurrentValue] = useState<Nullable<string | number>>(null);

    const inputElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (inputElementRef.current && autoFocus) {
            inputElementRef.current.focus();
        }
    }, [autoFocus]);

    function setInputElement(element: HTMLElement): void {
        inputElementRef.current = element;
    }

    function commitValue(value: string): void {
        if (!isControlled) {
            setCurrentValue(value);
        }
    }

    async function handleFocus(event: React.FocusEvent<HTMLInputElement>): Promise<void> {
        focusState.handleFocus();

        if (onFocus) {
            await onFocus(event);
        }
    }

    async function handleBlur(event: React.FocusEvent<HTMLInputElement>): Promise<void> {
        event.target.name = name ?? '';

        focusState.handleBlur();
        setCurrentValue(null);

        if (onBlur) {
            await onBlur(event);
        }
    }

    const inputValue = isNil(currentValue) ? (isNil(value) ? '' : value) : currentValue;

    return {
        isOnFocus: focusState.isOnFocus,
        inputValue,
        setInputElement,
        commitValue,
        handleFocus,
        handleBlur,
    };
}
